// Builds the contractually-meaningful name-cell subtitle (text + optional slip tag) for a milestone row.

import type { Milestone } from "../../../../types";
import {
  deriveMilestoneDisplayStatus,
  type MilestoneDisplayStatusResult,
} from "../../../../utils/deriveMilestoneDisplayStatus";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { daysBetween } from "./timelineGeometry";
import type { SlipTagVariant } from "./SlipTag";

export interface MilestoneRowSubtitle {
  text: string;
  slipTag?: { variant: SlipTagVariant; days: number };
}

function pickResolvedDisplay(
  m: Milestone,
  today: string,
): MilestoneDisplayStatusResult {
  return deriveMilestoneDisplayStatus(m, today);
}

export function milestoneRowSubtitle(
  m: Milestone,
  today: string,
): MilestoneRowSubtitle {
  const display = pickResolvedDisplay(m, today);
  const planned = m.plannedDate ? formatDayMonthYear(m.plannedDate) : null;

  if (display.riskOverlay === "late" && m.plannedDate) {
    const days = Math.max(0, daysBetween(m.plannedDate, today));
    return {
      text: `Due ${planned}`,
      slipTag: { variant: "late", days },
    };
  }

  // Late-history slip tag is derived once from plannedDate vs deliveredAt
  // and attached to whichever branch matches the lifecycle. Without this,
  // accepted-late and paid-late milestones lost their "▲ Nd late" tag the
  // moment payment was recorded — payment receipt does not erase the
  // contractual fact that the supplier delivered late.
  const deliveredLateSlip =
    display.riskOverlay === "delivered-late" && m.plannedDate && m.deliveredAt
      ? {
          variant: "deliveredLate" as const,
          days: Math.max(0, daysBetween(m.plannedDate, m.deliveredAt)),
        }
      : undefined;

  if (display.lifecycle === "paid" && m.paidAt) {
    const base = `Paid ${formatDayMonthYear(m.paidAt)}`;
    const text = m.deliveredAt
      ? `${base} · delivered ${formatDayMonthYear(m.deliveredAt)}`
      : base;
    return deliveredLateSlip ? { text, slipTag: deliveredLateSlip } : { text };
  }

  if (display.lifecycle === "accepted" && m.acceptedAt) {
    const days = Math.max(0, daysBetween(m.acceptedAt, today));
    const text = `Accepted ${formatDayMonthYear(m.acceptedAt)} · approval ${days} days ago`;
    return deliveredLateSlip ? { text, slipTag: deliveredLateSlip } : { text };
  }

  if (deliveredLateSlip && m.plannedDate && m.deliveredAt) {
    return {
      text: `Due ${planned} · delivered ${formatDayMonthYear(m.deliveredAt)}`,
      slipTag: deliveredLateSlip,
    };
  }

  if (display.lifecycle === "partially-accepted" && m.partiallyAcceptedAt) {
    return {
      text: `Partially accepted ${formatDayMonthYear(m.partiallyAcceptedAt)}`,
    };
  }

  if (display.lifecycle === "delivered" && m.deliveredAt) {
    return { text: `Delivered ${formatDayMonthYear(m.deliveredAt)}` };
  }

  if (planned) {
    return { text: `Due ${planned}` };
  }

  return { text: "" };
}
